import { axiosPlatform } from "../../_services/axios-helper";

/**
 * Registration API calls for the channel-first flow.
 *
 * The email path and the SSO path are DIFFERENT backend operations, each with its own
 * endpoint (SetupController):
 *  - Email path (POST /setup/register-channel-first): create org + profile with NO
 *    password, then trigger the activation email (user sets the password via the link).
 *    Powers success screen 4a.
 *  - SSO path (POST /setup/register-channel-first-sso): create an immediately-active
 *    account from the SSO identity, NO activation email, auto-login. Powers screen 4b.
 *
 * GTM/Meta conversion firing is intentionally NOT done here (deferred).
 */
const EMAIL_ENDPOINT = "/setup/register-channel-first";
const SSO_ENDPOINT = "/setup/register-channel-first-sso";

const commonFields = ({ companyName, phoneNumber, selectedChannels, analytics }) => ({
    organizationName: companyName.trim(),
    // Optional phone (ticket #42120) — persisted on the organisation. Omit when empty.
    phoneNumber: phoneNumber?.trim() || undefined,
    selectedChannels,
    // Legal acceptance is implied by the step-3 Nutzungsvereinbarung line.
    acceptTos: true,
    acceptDpa: true,
    // Attribution — referrer/source is used for channel pre-selection only for now.
    source: analytics?.newUserSource || undefined,
    entryReferrer: document.referrer || undefined,
});

/**
 * Email path — no password; backend sends the activation email.
 */
export const submitEmailRegistration = ({ companyName, phoneNumber, email, selectedChannels, analytics }) =>
    axiosPlatform().post(EMAIL_ENDPOINT, {
        ...commonFields({ companyName, phoneNumber, selectedChannels, analytics }),
        email: email.trim(),
        authProvider: "EMAIL",
    });

/**
 * SSO path — carries the Firebase ID token so the backend creates/links the identity
 * and activates the account immediately (no activation email).
 */
export const submitSsoRegistration = ({
    companyName,
    phoneNumber,
    selectedChannels,
    ssoAuth,
    analytics,
    createNewOrganizationForExistingUser = false,
}) => {
    const [firstName, ...rest] = (ssoAuth?.displayName || "").trim().split(" ");
    return axiosPlatform().post(SSO_ENDPOINT, {
        ...commonFields({ companyName, phoneNumber, selectedChannels, analytics }),
        email: ssoAuth?.email?.trim(),
        authProvider: ssoAuth?.provider,
        ssoIdToken: ssoAuth?.idToken,
        firstName: firstName || undefined,
        lastName: rest.length ? rest.join(" ") : undefined,
        // Set only after the user confirms the "account already exists" prompt.
        createNewOrganizationForExistingUser,
    });
};
